import type { JSX } from 'react';
import { useParams } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { useCandidateCertificate } from '../hooks/useCandidateCertificate';
import { CertificateHeader } from '../components/CertificateHeader';
import { CertificateTitle } from '../components/CertificateTitle';
import { CertificateExperienceCard } from '../components/CertificateExperienceCard';
import { DownloadPdfButton } from '../components/DownloadPdfButton';
import type { CandidateCertificate } from '../types/certificate.types';

const NOT_FOUND_MESSAGE = 'Certificate not found.';
const EMPTY_MESSAGE = 'No confirmed work experience has been recorded yet.';
const FOOTNOTE =
  'This document lists only work experience confirmed by at least one reference contact through Career Verification.';

function CertificateNotice({ message }: { message: string }): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4">
      <p className="max-w-md text-center text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function CertificateBody({ certificate }: { certificate: CandidateCertificate }): JSX.Element {
  if (certificate.experiences.length === 0) {
    return <p className="text-center text-sm text-muted-foreground">{EMPTY_MESSAGE}</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      {certificate.experiences.map((experience, index) => (
        <CertificateExperienceCard
          key={`${experience.companyName}-${experience.jobTitle}-${index}`}
          experience={experience}
        />
      ))}
    </div>
  );
}

export function CertificatePage(): JSX.Element {
  const { shareId } = useParams<{ shareId: string }>();
  const { certificate, isLoading, isError } = useCandidateCertificate(shareId ?? '');

  if (isLoading) {
    return <CertificateNotice message="Loading certificate..." />;
  }

  if (isError || !certificate) {
    return <CertificateNotice message={NOT_FOUND_MESSAGE} />;
  }

  return (
    <div className="min-h-screen bg-muted/40">
      <CertificateHeader />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-8 sm:py-12">
        <div className="mb-4 flex justify-end print:hidden">
          <DownloadPdfButton />
        </div>
        <article className="rounded-xl border border-border bg-card shadow-lg">
          <div className="m-3 flex flex-col gap-8 rounded-lg border border-border/50 p-5 sm:m-4 sm:p-10">
            <CertificateTitle candidateName={certificate.candidateName} />
            <Separator />
            <CertificateBody certificate={certificate} />
          </div>
        </article>
        <p className="mt-6 text-center text-xs text-muted-foreground">{FOOTNOTE}</p>
      </main>
    </div>
  );
}
